import { Link } from "@tanstack/react-router";
import { Home, ChevronRight } from "lucide-react";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

export type Crumb =
  | { label: string; to: "/" | "/categories" | "/collections" | "/discoveries" | "/play" | "/explore" | "/trending" | "/shop" }
  | { label: string; to: "/category/$slug"; params: { slug: string } }
  | { label: string; current: true };



interface Props {
  items: Crumb[];
  className?: string;
}

/**
 * Global breadcrumb trail. Always starts with Home for a guaranteed return path.
 * Compact, low-contrast styling so it doesn't compete with the page title.
 */
export function Breadcrumbs({ items, className = "" }: Props) {
  const [lang] = useLocale();
  const home = curatedUiTranslation(lang, "Home") ?? "Home";
  return (
    <nav
      aria-label={lang === "sv" ? "Brödsmulor" : lang === "zh" ? "面包屑导航" : "Breadcrumb"}
      className={`flex flex-wrap items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground ${className}`}
    >
      <Link to="/" className="inline-flex items-center gap-1 hover:text-primary">
        <Home className="h-3 w-3" /> {home}
      </Link>
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} className="inline-flex items-center gap-1.5">
            <ChevronRight className="h-3 w-3 opacity-50" />
            {"current" in item ? (
              <span aria-current="page" className="max-w-[60vw] truncate text-foreground md:max-w-md">
                {item.label}
              </span>
            ) : "params" in item ? (
              <Link
                to={item.to}
                params={item.params}
                className={isLast ? "text-foreground hover:text-primary" : "hover:text-primary"}
              >
                {item.label}
              </Link>
            ) : (
              <Link to={item.to} className={isLast ? "text-foreground hover:text-primary" : "hover:text-primary"}>
                {item.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
